// Thin wrapper around TierWrapUpDisplay that handles all the
// lesson-flow side effects (Redux state, navigate, the debug-load
// path). Non-lesson surfaces (the admin Play Sessions deep-dive)
// import TierWrapUpDisplay directly and supply props themselves.

import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LessonReducer } from 'Reducers/lessonReducer';
import { Box } from '@mui/system';
import { CustomButton } from 'Components/StyledComponents';
import { PlaySessionData } from 'Types/LessonTypes';
import { mediaQueryValues } from 'Utils/MediaQueries';
import { useMediaQueries } from 'Hooks/useMediaQueries';
import { MainAppReducer } from 'Types';
import { useNavigate } from 'react-router-dom';
import { getPlaySessionSummary } from 'Sagas/Lesson';
import { getCurrentAuthToken } from 'Utils/Auth';
import * as lessonActions from 'Actions/lesson';
import { AppDispatch } from 'Reducers';
import TierWrapUpDisplay from './TierWrapUpDisplay';

// ── Debug fixtures (kept in the wrapper because only the debug path
// uses them; not part of the presentational contract) ─────────────────

type PhraseDatum = {
  datetime: string;
  averageAccuracy: string;
  averageLHAccuracy: string;
  averageRHAccuracy: string;
};

type SessionData = {
  phraseData: PhraseDatum[];
  playTime: number;
  errorCount: number;
  totalOffTime: number;
  totalNotes: number;
};

const _testSessionData: SessionData = {
  phraseData: [
    {
      datetime: '2024-08-28T02:38:56.106Z',
      averageAccuracy: '98.02',
      averageLHAccuracy: '99.1',
      averageRHAccuracy: '97',
    },
    {
      datetime: '2024-08-28T02:39:09.459Z',
      averageAccuracy: '92.96',
      averageLHAccuracy: '85',
      averageRHAccuracy: '100',
    },
    {
      datetime: '2024-08-28T02:39:22.784Z',
      averageAccuracy: '92.34',
      averageLHAccuracy: '76',
      averageRHAccuracy: '92',
    },
    {
      datetime: '2024-08-28T02:39:36.108Z',
      averageAccuracy: '94.78',
      averageLHAccuracy: '95',
      averageRHAccuracy: '100',
    },
    {
      datetime: '2024-08-28T02:39:49.464Z',
      averageAccuracy: '97.92',
      averageLHAccuracy: '100',
      averageRHAccuracy: '97',
    },
    {
      datetime: '2024-08-28T02:40:02.780Z',
      averageAccuracy: '99.17',
      averageLHAccuracy: '98',
      averageRHAccuracy: '100',
    },
  ],
  playTime: 120013,
  errorCount: 5,
  totalOffTime: 5,
  totalNotes: 30,
};

const _playSessionData: PlaySessionData = {
  user_id: '3d1abf40-0bfb-4967-be24-2b8494e0f204',
  tier_number: 1,
  avg_running_accuracy: 53.19,
  avg_lh_running_accuracy: 57.37,
  avg_rh_running_accuracy: 48.73,
  avg_accuracy: 96,
  avg_lh_accuracy: 100,
  avg_rh_accuracy: 90.66,
  avg_error_score: 0.35,
  total_play_time: 40002,
  error_recognition_mode: 'MIDI',
  createdAt: '2025-04-27T05:48:35.863Z',
  updatedAt: '2025-04-27T05:49:32.388Z',
  deletedAt: null,
  level_number: 15,
  id: '212ce98c-3328-4031-959e-e64a169501e4',
  level_completed_event_id: null,
  tier_completed_event_id: '2d6c64a1-3c5e-4848-9401-2f0f8fbe0990',
  level_started_event_id: null,
  tier_started_event_id: null,
  tier_session_finished_event_id: null,
  semi_errors: 2,
  lh_semi_errors: 0,
  rh_semi_errors: 0,
  non_errors: 33,
  lh_non_errors: 20,
  rh_non_errors: 13,
  errors: 0,
  lh_errors: 0,
  rh_errors: 0,
  played_notes: 35,
  expected_notes: 35,
  rh_expected_notes: 15,
  lh_expected_notes: 20,
  additional_notes: 0,
  previousTiersPlaySessions: [],
} as unknown as PlaySessionData;

// Silence unused-var lint for the debug-only fixture kept for parity.
void _testSessionData;

type TierWrapUpProps = {
  mediaQueries: {
    maxHeight: mediaQueryValues.minHeight1100;
    maxWidth: mediaQueryValues.minWidth800;
  };
  debugShowTierSummary?: boolean;
};

const TierWrapUp = (props: TierWrapUpProps) => {
  const {
    tierSummary: tierSummaryData,
    currentUserLevelData,
    currentLevelTiers,
    lessonProcessPending,
    phrasesTilNextTier,
    tierSummaryRequestError,
  } = useSelector((state: LessonReducer) => state.lessonReducer);
  const { userData } = useSelector((state: MainAppReducer) => state.mainAppReducer);
  const { mediaQueries, debugShowTierSummary = false } = props;
  const dispatch = useDispatch<AppDispatch>();
  const { maxHeight } = useMediaQueries();
  const navigate = useNavigate();
  const [debugPlaySessionId, setDebugPlaySessionId] = useState('');

  const debugSetPlaySession = useCallback(async () => {
    const jwtToken = await getCurrentAuthToken();
    if (jwtToken && debugPlaySessionId) {
      const ts: PlaySessionData = await getPlaySessionSummary(jwtToken, debugPlaySessionId);
      dispatch(lessonActions.setTierSummary(ts));
    }
  }, [debugPlaySessionId, dispatch]);
  const tierSummary = (tierSummaryData ?? debugShowTierSummary) ? _playSessionData : null;

  // Reusable debug input panel rendered both in the error state and
  // inline above the success header.
  const debugControls = debugShowTierSummary ? (
    <Box sx={{ color: 'white', margin: '30px' }}>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '12px' }}>
        <input
          placeholder="Play session id"
          value={debugPlaySessionId}
          onChange={(e) => setDebugPlaySessionId(e.target.value)}
          style={{
            background: '#fff',
            padding: '8px 12px',
            borderRadius: 6,
            border: '1px solid rgba(0,0,0,0.12)',
            minWidth: 260,
            color: '#000',
          }}
        />
        <CustomButton
          sx={{ textTransform: 'uppercase', height: '40px' }}
          onClick={async () => debugSetPlaySession()}
          disabled={!debugPlaySessionId}
        >
          Load
        </CustomButton>
        <CustomButton
          sx={{ textTransform: 'uppercase', height: '40px' }}
          onClick={() => setDebugPlaySessionId('')}
          disabled={!debugPlaySessionId}
        >
          Clear
        </CustomButton>
      </Box>
    </Box>
  ) : null;

  // Error / empty branch: only the debug controls (in debug mode) or
  // the generic error message.
  if (!tierSummary && !lessonProcessPending) {
    if (!tierSummaryRequestError) {
      return null;
    }
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          width: '100%',
        }}
      >
        {debugControls}
        {!debugShowTierSummary && (
          <h3>
            Something went wrong retrieving your wrap up data. We're working on it (sorry). If the
            problem persists, please reach out to support@museflow.ai
          </h3>
        )}
      </Box>
    );
  }

  return (
    <TierWrapUpDisplay
      tierSummary={tierSummary}
      currentUserLevelData={currentUserLevelData}
      currentLevelTiers={currentLevelTiers}
      phrasesTilNextTier={phrasesTilNextTier}
      colorblindType={userData?.colorblind_type || null}
      mediaQueries={mediaQueries}
      maxHeight={maxHeight}
      loading={lessonProcessPending}
      onBackClick={() => {
        console.log('[MUS-61] TierWrapUp button → /roadmap');
        navigate('/roadmap');
      }}
      backLabel="BACK TO ROADMAP"
      debugSlot={debugControls}
      showSessionIdFooter={process.env.REACT_APP_NODE_ENV !== 'prd'}
    />
  );
};

export default TierWrapUp;
